import { getFileDownloadURL } from '../firebase/r2storage';

/**
 * Helper to refresh expired R2 URLs
 * This utility helps convert old presigned URLs to fresh ones
 */

// Extract fileKey from various URL formats
export const extractFileKeyFromUrl = (url) => {
  if (!url) return null;
  
  try {
    // If it's already a fileKey (no domain), return as is
    if (!url.startsWith('http')) {
      return url;
    }
    
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    
    // Handle presigned URLs (contains X-Amz-Signature)
    if (url.includes('X-Amz-Signature')) {
      // Remove leading slash
      let fileKey = pathname.substring(1);
      
      // Remove bucket name if present in path
      const parts = fileKey.split('/');
      if (parts.length > 1 && parts[0] === 'mahstry-storage') {
        fileKey = parts.slice(1).join('/');
      }
      
      return fileKey;
    }
    
    // Handle r2.dev URLs
    if (url.includes('.r2.dev/')) {
      const match = url.match(/\.r2\.dev\/([^?]+)/);
      if (match) {
        let fileKey = match[1];
        // Remove bucket name if present
        const parts = fileKey.split('/');
        if (parts.length > 1 && parts[0] === 'mahstry-storage') {
          fileKey = parts.slice(1).join('/');
        }
        return fileKey;
      }
    }
    
    // Handle cloudflarestorage.com URLs
    if (url.includes('.r2.cloudflarestorage.com/')) {
      const match = url.match(/\.r2\.cloudflarestorage\.com\/([^?]+)/);
      if (match) {
        let fileKey = match[1];
        // Remove bucket name if present
        const parts = fileKey.split('/');
        if (parts.length > 1 && parts[0] === 'mahstry-storage') {
          fileKey = parts.slice(1).join('/');
        }
        return fileKey;
      }
    }
    
    // Generic fallback: extract pathname
    let fileKey = pathname.substring(1); // Remove leading slash
    const parts = fileKey.split('/');
    if (parts.length > 1 && parts[0] === 'mahstry-storage') {
      fileKey = parts.slice(1).join('/');
    }
    
    return fileKey || null;
  } catch (error) {
    console.error('Error extracting fileKey from URL:', error);
    return null;
  }
};

/**
 * Check if a URL is expired or about to expire
 * @param {string} url - The URL to check
 * @returns {boolean} - True if expired or expiring soon
 */
export const isUrlExpired = (url) => {
  if (!url) return true;
  
  try {
    // If it's not a presigned URL, assume it might be expired
    if (!url.includes('X-Amz-Signature')) {
      return false; // Not a presigned URL, can't determine expiration
    }
    
    const urlObj = new URL(url);
    const expiresParam = urlObj.searchParams.get('X-Amz-Expires');
    const dateParam = urlObj.searchParams.get('X-Amz-Date');
    
    if (!expiresParam || !dateParam) {
      return true; // Can't determine, assume expired
    }
    
    // Parse the date (format: YYYYMMDDTHHMMSSZ)
    const year = parseInt(dateParam.substring(0, 4));
    const month = parseInt(dateParam.substring(4, 6)) - 1;
    const day = parseInt(dateParam.substring(6, 8));
    const hour = parseInt(dateParam.substring(9, 11));
    const minute = parseInt(dateParam.substring(11, 13));
    const second = parseInt(dateParam.substring(13, 15));
    
    const signedDate = new Date(Date.UTC(year, month, day, hour, minute, second));
    const expiresInSeconds = parseInt(expiresParam);
    const expirationDate = new Date(signedDate.getTime() + expiresInSeconds * 1000);
    
    // Consider expired if less than 1 hour remaining
    const oneHourFromNow = new Date(Date.now() + 3600000);
    return expirationDate < oneHourFromNow;
  } catch (error) {
    console.error('Error checking URL expiration:', error);
    return true; // Assume expired on error
  }
};

/**
 * Refresh a URL if it's expired
 * @param {string} urlOrKey - Either a URL or fileKey
 * @param {number} expiresIn - Expiration time in seconds (default: 7 days, max: 7 days for R2)
 * @returns {Promise<string|null>} - Fresh URL or null if failed
 */
export const refreshUrl = async (urlOrKey, expiresIn = 604800) => {
  if (!urlOrKey) return null;
  
  try {
    // Check if it's already expired
    const expired = isUrlExpired(urlOrKey);
    
    // Extract fileKey
    let fileKey = urlOrKey;
    if (urlOrKey.startsWith('http')) {
      fileKey = extractFileKeyFromUrl(urlOrKey);
      if (!fileKey) {
        console.error('Could not extract fileKey from URL:', urlOrKey);
        return null;
      }
    }
    
    // If not expired and it's a valid URL, return as is
    if (!expired && urlOrKey.startsWith('http')) {
      return urlOrKey;
    }
    
    // Get fresh URL
    const result = await getFileDownloadURL(fileKey, expiresIn);
    if (result.success) {
      return result.url;
    }
    
    return null;
  } catch (error) {
    console.error('Error refreshing URL:', error);
    return null;
  }
};

/**
 * Refresh multiple URLs in batch
 * @param {Array<string>} urlsOrKeys - Array of URLs or fileKeys
 * @param {number} expiresIn - Expiration time in seconds
 * @returns {Promise<Array<string|null>>} - Array of fresh URLs
 */
export const refreshUrlsBatch = async (urlsOrKeys, expiresIn = 604800) => {
  if (!Array.isArray(urlsOrKeys)) return [];
  
  const promises = urlsOrKeys.map(urlOrKey => refreshUrl(urlOrKey, expiresIn));
  return await Promise.all(promises);
};

/**
 * Refresh URLs in an object recursively
 * @param {Object} obj - Object containing URLs
 * @param {Array<string>} urlFields - Field names that contain URLs
 * @returns {Promise<Object>} - Object with refreshed URLs
 */
export const refreshUrlsInObject = async (obj, urlFields = ['url', 'fileUrl', 'videoUrl', 'imageUrl', 'attachmentUrl']) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const refreshed = { ...obj };
  
  for (const field of urlFields) {
    if (refreshed[field]) {
      const freshUrl = await refreshUrl(refreshed[field]);
      if (freshUrl) {
        refreshed[field] = freshUrl;
      }
    }
  }
  
  return refreshed;
};

/**
 * Refresh URLs in an array of objects
 * @param {Array<Object>} items - Array of objects containing URLs
 * @param {Array<string>} urlFields - Field names that contain URLs
 * @returns {Promise<Array<Object>>} - Array with refreshed URLs
 */
export const refreshUrlsInArray = async (items, urlFields = ['url', 'fileUrl', 'videoUrl', 'imageUrl', 'attachmentUrl']) => {
  if (!Array.isArray(items)) return items;
  
  const promises = items.map(item => refreshUrlsInObject(item, urlFields));
  return await Promise.all(promises);
};

export default {
  extractFileKeyFromUrl,
  isUrlExpired,
  refreshUrl,
  refreshUrlsBatch,
  refreshUrlsInObject,
  refreshUrlsInArray,
};
